import { useEffect, useState } from "react";
import { Button } from "../../tailwind/button";
import { Input } from "../../tailwind/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../tailwind/dialog";

// Validates: +<country code 1-3 digits> <10 digit phone number>
const isPhoneValid = (num) => {
  const trimmed = num.trim();
  if (!trimmed.startsWith('+')) return false;
  const withoutPlus = trimmed.slice(1);
  const parts = withoutPlus.trim().split(/\s+/);
  if (parts.length < 2) {
    // No space: check total digits are 11-13 (1-3 country + 10 phone)
    const digits = withoutPlus.replace(/\D/g, '');
    return digits.length >= 11 && digits.length <= 13;
  }
  const countryCode = parts[0];
  const phoneDigits = parts.slice(1).join('').replace(/\D/g, '');
  return /^\d{1,3}$/.test(countryCode) && phoneDigits.length === 10;
};

export default function StartConversationDialog({
  open,
  onOpenChange,
  phones = [],
  onSubmit,
  isLoading = false,
}) {
  const [channelId, setChannelId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Automatically select the first phone number when list loads
  useEffect(() => {
    if (phones.length > 0 && !channelId) {
      setChannelId(phones[0].wabaPhoneId);
    }
  }, [phones, channelId]);

  const handleStartChat = () => {
    if (!phoneNumber.trim() || !channelId) return;
    
    // Call onSubmit callback
    onSubmit?.({
      channelId,
      phoneNumber: phoneNumber.replace(/\s/g, ''),
    });
    
    // Reset form fields
    setPhoneNumber("");
    onOpenChange?.(false);
  };

  const isFormValid = isPhoneValid(phoneNumber) && channelId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-0 shadow-lg text-gray-900 dark:text-slate-100 p-6 rounded-xl">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Start New Conversation
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 dark:text-slate-400">
            Enter a phone number to start a new WhatsApp conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Send From Field */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700 dark:text-slate-200">
              Send From
            </label>
            <div className="relative">
              {phones.length === 0 ? (
                <div className="text-xs text-amber-600 dark:text-amber-400 py-2">
                  No configured phone numbers found. Please check your settings.
                </div>
              ) : (
                <select
                  value={channelId}
                  onChange={(e) => setChannelId(e.target.value)}
                  className="w-full h-10 px-3 bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-lg text-sm text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {phones.map((phone) => (
                    <option key={phone.wabaPhoneId} value={phone.wabaPhoneId}>
                      {phone.displayPhoneNumber} ({phone.displayName})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* To Phone Number Field */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700 dark:text-slate-200">
              To Phone Number <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              placeholder="+91 9876543210"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}

              className="bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800 h-10 rounded-lg text-gray-900 dark:text-slate-100 pl-3 focus-visible:ring-blue-500"
              disabled={isLoading}
            />
            {phoneNumber.trim() && !isPhoneValid(phoneNumber) ? (
              <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                Enter a valid number: + country code followed by 10 digits (e.g. +919876543210)
              </p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                Include country code (e.g. +91)
              </p>
            )}
          </div>

        </div>

        <DialogFooter className="flex gap-2 sm:justify-end border-t border-gray-100 dark:border-slate-800/60 pt-4 mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            className="h-10 px-4 border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleStartChat}
            disabled={!isFormValid || isLoading}
            className="h-10 px-4 bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] disabled:opacity-50 text-white rounded-lg transition-colors font-semibold"
          >
            {isLoading ? "Starting..." : "Start Chat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
